import { useCallback, useEffect, useRef, useState } from 'react';
import { makeApiCall } from '../../config/api';

const STORAGE_KEY = 'assistant.conversationId';

let localIdCounter = 0;
const localId = (prefix) => {
  localIdCounter += 1;
  return `${prefix}-${Date.now()}-${localIdCounter}`;
};

/**
 * Parse a text/event-stream body from fetch. Calls onEvent(name, data) for
 * each event frame. Resolves when the stream ends.
 */
async function readSse(response, onEvent) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const dispatch = (frame) => {
    let eventName = 'message';
    const dataLines = [];
    frame.split('\n').forEach((line) => {
      if (line.startsWith(':')) return;
      if (line.startsWith('event:')) eventName = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
    });
    if (!dataLines.length) return;
    let data = null;
    try {
      data = JSON.parse(dataLines.join('\n'));
    } catch (error) {
      data = { raw: dataLines.join('\n') };
    }
    onEvent(eventName, data);
  };
  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let separator = buffer.indexOf('\n\n');
    while (separator !== -1) {
      const frame = buffer.slice(0, separator);
      buffer = buffer.slice(separator + 2);
      if (frame.trim()) dispatch(frame.replace(/\r/g, ''));
      separator = buffer.indexOf('\n\n');
    }
  }
  if (buffer.trim()) dispatch(buffer.replace(/\r/g, ''));
}

/**
 * Normalize a stored message from GET /conversations/:id/messages into the
 * shape the panel renders.
 */
function fromServerMessage(row) {
  if (row.action) {
    return {
      id: `action-${row.action.id}`,
      role: 'action',
      action: {
        id: row.action.id,
        status: row.action.status,
        summary: row.action.summary,
        tool: row.action.tool,
        arguments: row.action.arguments,
      },
      created_at: row.created_at,
    };
  }
  return {
    id: `m-${row.id}`,
    role: row.role,
    content: row.content || '',
    created_at: row.created_at,
    activity: [],
    navigations: [],
    done: true,
  };
}

export default function useAssistantChat({ locale, getPageContext, onNavigate, onUnread }) {
  const [conversationId, setConversationId] = useState(() => {
    try {
      const stored = window.sessionStorage.getItem(STORAGE_KEY);
      return stored ? Number(stored) : null;
    } catch (error) {
      return null;
    }
  });
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState('');
  const [loadingHistory, setLoadingHistory] = useState(false);
  const abortRef = useRef(null);
  const onNavigateRef = useRef(onNavigate);
  const onUnreadRef = useRef(onUnread);
  onNavigateRef.current = onNavigate;
  onUnreadRef.current = onUnread;

  useEffect(() => {
    try {
      if (conversationId) window.sessionStorage.setItem(STORAGE_KEY, String(conversationId));
      else window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore storage failures
    }
  }, [conversationId]);

  const updateMessage = useCallback((id, updater) => {
    setMessages((prev) => prev.map((message) => (message.id === id ? updater(message) : message)));
  }, []);

  const refreshConversations = useCallback(async () => {
    try {
      const response = await makeApiCall('api/agent/conversations?limit=30');
      if (!response.ok) return;
      const payload = await response.json();
      setConversations(payload.conversations || []);
    } catch (e) {
      // non-fatal
    }
  }, []);

  const loadConversation = useCallback(async (id) => {
    if (!id) {
      setMessages([]);
      setConversationId(null);
      return;
    }
    setLoadingHistory(true);
    setError('');
    try {
      const response = await makeApiCall(`api/agent/conversations/${id}/messages`);
      if (response.status === 404) {
        setConversationId(null);
        setMessages([]);
        return;
      }
      if (!response.ok) throw new Error('load_failed');
      const payload = await response.json();
      setMessages((payload.messages || []).map(fromServerMessage));
      setConversationId(id);
    } catch (e) {
      setError('load_failed');
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Restore the last conversation on mount.
  useEffect(() => {
    if (conversationId) loadConversation(conversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newConversation = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setConversationId(null);
    setMessages([]);
    setError('');
  }, []);

  const deleteConversation = useCallback(async (id) => {
    try {
      await makeApiCall(`api/agent/conversations/${id}`, { method: 'DELETE' });
    } finally {
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (id === conversationId) newConversation();
    }
  }, [conversationId, newConversation]);

  /**
   * Shared SSE consumer for /chat and /actions/:id/confirm. Creates a
   * streaming assistant placeholder and fills it as events arrive.
   */
  const consumeStream = useCallback(async (endpoint, body) => {
    const assistantId = localId('assistant');
    setMessages((prev) => [...prev, {
      id: assistantId, role: 'assistant', content: '', activity: [], navigations: [], done: false,
    }]);
    setStreaming(true);
    setError('');
    const controller = new AbortController();
    abortRef.current = controller;
    let sawDone = false;
    try {
      const response = await makeApiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!response.ok || !response.body) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || `HTTP ${response.status}`);
      }
      await readSse(response, (eventName, data) => {
        switch (eventName) {
          case 'conversation':
            if (data?.id) {
              setConversationId(data.id);
            }
            break;
          case 'status':
            updateMessage(assistantId, (m) => ({
              ...m,
              activity: [...m.activity, { id: localId('act'), tool: data.tool, category: data.category, risk: data.risk, state: 'running' }],
            }));
            break;
          case 'tool_result':
            updateMessage(assistantId, (m) => {
              const activity = [...m.activity];
              for (let index = activity.length - 1; index >= 0; index -= 1) {
                if (activity[index].tool === data.tool && activity[index].state === 'running') {
                  activity[index] = { ...activity[index], state: data.ok ? 'ok' : 'error', error: data.error || null };
                  break;
                }
              }
              return { ...m, activity };
            });
            break;
          case 'delta':
            updateMessage(assistantId, (m) => ({ ...m, content: `${m.content}${data?.text || ''}` }));
            break;
          case 'confirmation_required':
            setMessages((prev) => {
              const index = prev.findIndex((m) => m.id === assistantId);
              const card = {
                id: `action-${data.action_id}`,
                role: 'action',
                action: {
                  id: data.action_id, status: 'pending', summary: data.summary, tool: data.tool, arguments: data.arguments, expires_at: data.expires_at,
                },
              };
              if (index === -1) return [...prev, card];
              return [...prev.slice(0, index), card, ...prev.slice(index)];
            });
            updateMessage(assistantId, (m) => ({
              ...m,
              activity: m.activity.map((a) => (a.tool === data.tool && a.state === 'running' ? { ...a, state: 'pending' } : a)),
            }));
            break;
          case 'navigate':
            updateMessage(assistantId, (m) => ({ ...m, navigations: [...m.navigations, data] }));
            if (onNavigateRef.current) onNavigateRef.current(data);
            break;
          case 'action_resolved':
            updateMessage(`action-${data.action_id}`, (m) => ({ ...m, action: { ...m.action, status: data.status } }));
            break;
          case 'done':
            sawDone = true;
            if (data?.conversation_id) setConversationId(data.conversation_id);
            break;
          case 'error':
            setError(data?.message || 'error');
            updateMessage(assistantId, (m) => ({ ...m, error: data?.message || 'error' }));
            break;
          default:
            break;
        }
      });
      if (sawDone && onUnreadRef.current) onUnreadRef.current();
    } catch (e) {
      if (e.name !== 'AbortError') {
        setError(e.message || 'error');
        updateMessage(assistantId, (m) => ({ ...m, error: e.message || 'error' }));
      }
    } finally {
      updateMessage(assistantId, (m) => ({
        ...m,
        done: true,
        activity: m.activity.map((a) => (a.state === 'running' ? { ...a, state: 'error' } : a)),
      }));
      setStreaming(false);
      if (abortRef.current === controller) abortRef.current = null;
      refreshConversations();
    }
  }, [refreshConversations, updateMessage]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || streaming) return;
    setMessages((prev) => [...prev, { id: localId('user'), role: 'user', content: trimmed, done: true }]);
    await consumeStream('api/agent/chat', {
      conversation_id: conversationId || undefined,
      message: trimmed,
      locale,
      page_context: getPageContext ? getPageContext() : null,
    });
  }, [consumeStream, conversationId, getPageContext, locale, streaming]);

  const confirmAction = useCallback(async (actionId) => {
    if (streaming) return;
    updateMessage(`action-${actionId}`, (m) => ({ ...m, action: { ...m.action, status: 'running' } }));
    await consumeStream(`api/agent/actions/${actionId}/confirm`, {
      locale,
      page_context: getPageContext ? getPageContext() : null,
    });
    // If the server never told us the outcome (e.g. network drop), fall back to a neutral state.
    updateMessage(`action-${actionId}`, (m) => (
      m.action.status === 'running' ? { ...m, action: { ...m.action, status: 'failed' } } : m
    ));
  }, [consumeStream, getPageContext, locale, streaming, updateMessage]);

  const cancelAction = useCallback(async (actionId) => {
    try {
      const response = await makeApiCall(`api/agent/actions/${actionId}/cancel`, { method: 'POST', body: JSON.stringify({}) });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || `HTTP ${response.status}`);
      }
      updateMessage(`action-${actionId}`, (m) => ({ ...m, action: { ...m.action, status: 'cancelled' } }));
    } catch (e) {
      setError(e.message || 'error');
    }
  }, [updateMessage]);

  const stop = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  return {
    conversationId,
    conversations,
    messages,
    streaming,
    loadingHistory,
    error,
    clearError: () => setError(''),
    sendMessage,
    confirmAction,
    cancelAction,
    stop,
    newConversation,
    loadConversation,
    deleteConversation,
    refreshConversations,
  };
}
